/**
 * useCallEvents — WebSocket composable для получения событий звонков в реальном времени
 * Подключается к /api/v1/telephony/ws и пушит события в telephony store
 */
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { API_CONFIG } from '@/api/config'
import { useTelephonyStore } from '@/features/telephony/stores/telephony.store'
import type { CallEvent } from '@/features/telephony/types/telephony.types'
import { isImpersonating, tokenStorage } from '@/features/auth/tokenStorage'

const RECONNECT_DELAY_MS = 3000
const MAX_RECONNECT_ATTEMPTS = 10

export function useCallEvents() {
  const store = useTelephonyStore()
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)

  let ws: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  function getWsUrl(): string {
    const base = API_CONFIG.BASE_URL
    const protocol = base.startsWith('https') ? 'wss' : 'ws'
    const host = base.replace(/^https?:\/\//, '')
    const token = tokenStorage.getAccessToken() || ''
    return `${protocol}://${host}/api/v1/telephony/ws?token=${encodeURIComponent(token)}`
  }

  function connect() {
    if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
      return
    }

    // Вкладка просмотра «глазами пользователя» (DEV-2399): звонки пользователя
    // в браузер администратора не идут, а сервер такой сокет и не примет.
    if (isImpersonating()) return

    const token = tokenStorage.getAccessToken()
    if (!token) {
      console.warn('[CallEvents] No access token, skipping WS connection')
      return
    }

    const url = getWsUrl()
    ws = new WebSocket(url)

    ws.onopen = () => {
      isConnected.value = true
      reconnectAttempts.value = 0
      console.debug('[CallEvents] WebSocket connected')
    }

    ws.onmessage = (event) => {
      try {
        const callEvent: CallEvent = JSON.parse(event.data)
        store.handleCallEvent(callEvent)
      } catch (e) {
        console.error('[CallEvents] Failed to parse WS message:', e)
      }
    }

    ws.onclose = () => {
      isConnected.value = false
      ws = null
      console.debug('[CallEvents] WebSocket disconnected')
      scheduleReconnect()
    }

    ws.onerror = (err) => {
      console.error('[CallEvents] WebSocket error:', err)
    }
  }

  function scheduleReconnect() {
    if (reconnectAttempts.value >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[CallEvents] Max reconnect attempts reached')
      return
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
    }
    reconnectTimer = setTimeout(() => {
      reconnectAttempts.value++
      console.debug(`[CallEvents] Reconnecting (attempt ${reconnectAttempts.value})...`)
      connect()
    }, RECONNECT_DELAY_MS)
  }

  function disconnect() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
    reconnectAttempts.value = MAX_RECONNECT_ATTEMPTS
    if (ws) {
      ws.close()
      ws = null
    }
    isConnected.value = false
  }

  onMounted(() => {
    connect()
  })

  onBeforeUnmount(() => {
    disconnect()
  })

  return {
    isConnected,
    connect,
    disconnect,
  }
}
